// Uses react because creator-app uses it. Convert to preact when can.
// This directive allows it to work in creator app. Without it *I think* it's referencing
// the frontend-common tsconfig file and converting to preact.
import type { ComponentChildren } from "preact";
import { useContext, createContext, useMemo, useState } from "preact/compat";

type Language = {
	readonly name: string;
	readonly code: string;
};

type LanguageTranslation = Language & {
	readonly translations: Record<string, string> | undefined;
};

type ContextValue = {
	readonly translate: (
		value: string,
		variables?: Record<string, string>,
	) => string;
	readonly languageOptions: readonly Language[];
	readonly setCurrentLanguage: (newLanguage: Language) => void;
	readonly currentLanguage: Language;
};

const i18nContext = createContext<ContextValue | undefined>(undefined);

const storageKeyName = "language";

// Replace all whitespace blocks with a single space
function normaliseTextKey(text: string) {
	return text.replace(/\s+/g, " ");
}

function replaceVariables(
	text: string,
	variables: Record<string, string> | undefined,
) {
	if (!variables) {
		return text;
	}

	return text.replace(/\{([a-zA-Z0-9_]+)\}/g, (_, variableName: string) => {
		const value = variables[variableName];
		if (value === undefined) {
			throw new Error(`Unknown variable "${variableName}"`);
		}
		return String(value);
	});
}

type I18nProviderProps = {
	readonly translations: readonly LanguageTranslation[];
	readonly children: ComponentChildren;
};

function I18nProvider({ translations, children }: I18nProviderProps) {
	const defaultTranslation = translations[0];
	const [currentLanguageCode, setCurrentLanguageCode] = useState<string>(() => {
		const stored = localStorage.getItem(storageKeyName);
		if (stored && translations.some((t) => t.code === stored)) {
			return stored;
		}
		const [browserLanguageCode] = navigator.language.split("-");
		if (translations.some((t) => t.code === browserLanguageCode)) {
			return browserLanguageCode;
		}
		return defaultTranslation.code;
	});

	const value = useMemo(() => {
		const currentLanguage = translations.find(
			(t) => t.code === currentLanguageCode,
		);
		if (currentLanguage === undefined) {
			throw new Error(`No language with code ${currentLanguageCode}`);
		}
		return {
			currentLanguage,
			translate(text: string, variables?: Record<string, string>) {
				const languageTranslations = translations.find(
					(t) => t.code === currentLanguageCode,
				)?.translations;
				if (languageTranslations === undefined) {
					if (currentLanguageCode !== defaultTranslation.code) {
						console.error(`No translations found for`, currentLanguageCode);
					}
					return replaceVariables(text, variables);
				}

				const key = normaliseTextKey(text);
				const translation = languageTranslations[key];
				if (translation === undefined) {
					if (currentLanguageCode !== defaultTranslation.code) {
						console.warn(
							`No translations for "${key}" in language "${currentLanguageCode}"`,
						);
					}
					return replaceVariables(text, variables);
				}

				return replaceVariables(translation, variables);
			},
			languageOptions: translations,
			setCurrentLanguage(newLanguage: Language) {
				setCurrentLanguageCode(newLanguage.code);
				localStorage.setItem(storageKeyName, newLanguage.code);
			},
		};
	}, [currentLanguageCode]);
	return <i18nContext.Provider value={value}>{children}</i18nContext.Provider>;
}

function useTranslator() {
	const value = useContext(i18nContext);
	if (value === undefined) {
		throw new Error("useTranslator must be used within a I18nProvider");
	}

	return value.translate;
}

function useTranslationControls() {
	const value = useContext(i18nContext);
	if (value === undefined) {
		throw new Error(
			"useTranslationControls must be used within a I18nProvider",
		);
	}

	const { translate, ...valueRest } = value;
	return valueRest;
}

export { useTranslator, useTranslationControls, I18nProvider };
